// src/components/UserProfile.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

export const UserProfile = ({ isCollapsed = false }) => {
  const { currentUser, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/dashboard");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!currentUser) return null;

  // Avatar sizes
  const avatarPx = isCollapsed ? 24 : 28;
  // No border on the avatar — it was rendering as a stark white ring
  // (Tailwind's default `border` falls back to zinc-200, which has no
  // dark-mode variant set on this element) against the navbar's dark
  // surface. The round shape + image content is enough visual separation.
  const avatarClass = "rounded-full object-cover";

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setIsMenuOpen((v) => !v)}
        className="
          w-full flex items-center gap-2 rounded-md
          px-2 py-2 transition-colors
          hover:bg-zinc-100 dark:hover:bg-zinc-900/60
        "
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
      >
        <div
          className="shrink-0"
          style={{ width: avatarPx, height: avatarPx }}
        >
          {currentUser.photoURL ? (
            <img
              src={currentUser.photoURL}
              alt="Profile"
              className={avatarClass}
              style={{ width: "100%", height: "100%" }}
            />
          ) : (
            <div
              className={`${avatarClass} flex items-center justify-center text-[11px]`}
              style={{ width: "100%", height: "100%", backgroundColor: 'var(--color-bg-muted)', color: 'var(--color-text)' }}
            >
              {currentUser.displayName?.charAt(0).toUpperCase() ||
                currentUser.email?.charAt(0).toUpperCase() ||
                "U"}
            </div>
          )}
        </div>

        {/* Only show name when expanded. Email is intentionally NOT shown
            in the navbar — it duplicates info that already lives one click
            away (the dropdown menu header shows name + email together for
            account-confirmation context). Keeping the navbar to a single
            line keeps the sidebar compact and reads cleaner. */}
        {!isCollapsed && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <div className="text-sm truncate" style={{ color: 'var(--color-text)' }}>
                {currentUser.displayName || currentUser.email}
              </div>
            </div>
            {/* Chevron — rotates 180° when the menu opens so the affordance
                is obvious. Default points down (familiar "click to reveal"
                pattern); flips up when the menu is open above the button.
                Note: explicit `rotate-0` when closed so CSS has two
                transform values to interpolate between (it can't animate
                from `transform: none` to `rotate(180deg)`). */}
            <ChevronDown
              className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : 'rotate-0'}`}
              style={{ color: 'var(--color-text-muted)' }}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      {/* Menu (appears above the user button). AnimatePresence keeps the
          element mounted long enough for the exit animation to play, then
          unmounts. The menu emerges from the button (starts slightly down
          + transparent, slides up into final position above) so it reads
          like it's spawning from the trigger. */}
      <AnimatePresence>
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          // origin-bottom anchors the scale to the bottom edge (which sits
          // against the button) so the menu appears to grow out of the
          // button instead of scaling from its center.
          style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', transformOrigin: 'bottom left' }}
          className="absolute bottom-full left-0 mb-2 w-56 rounded-xl border shadow-xl overflow-hidden z-[2000]"
          onClick={(e) => e.stopPropagation()}
          role="menu"
          aria-label="User menu"
        >
          <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div className="text-sm font-medium truncate" style={{ color: 'var(--color-text)' }}>
              {currentUser.displayName || "User"}
            </div>
            <div className="text-[12px] truncate" style={{ color: 'var(--color-text-muted)' }}>
              {currentUser.email || ""}
            </div>
          </div>

          <div className="py-1 text-sm">
            <Link
              to="/account"
              className="block px-3 py-2 hover:bg-[var(--color-card-hover)]"
              style={{ color: 'var(--color-text)' }}
              onClick={() => setIsMenuOpen(false)}
              role="menuitem"
            >
              Account
            </Link>
            <Link
              to="/settings"
              className="block px-3 py-2 hover:bg-[var(--color-card-hover)]"
              style={{ color: 'var(--color-text)' }}
              onClick={() => setIsMenuOpen(false)}
              role="menuitem"
            >
              Settings
            </Link>
            <button
              onClick={handleSignOut}
              className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              role="menuitem"
            >
              Sign out
            </button>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfile;